import React, { useEffect, useRef, useState, useCallback } from 'react';
import { images } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const wineImages = [
  { src: images.vini, alt: "Vini - Bollicine, Aperitivi, Bianchi, Rosati, Rossi" },
  { src: images.viniSpumanti, alt: "Vini Spumanti in Bottiglia" },
  { src: images.viniBianchi, alt: "Vini Bianchi in Bottiglia" },
  { src: images.viniRosati, alt: "Vini Rosati in Bottiglia" },
  { src: images.viniRossi1, alt: "Vini Rossi - San Zeno, Roncaia, Dolcetto d'Alba" },
  { src: images.viniRossi2, alt: "Vini Rossi - Il Bruciato, Insoglio, Volpettona" },
  { src: images.viniRossiTicinesi, alt: "Carta dei Vini - Rossi Ticinesi" },
  { src: images.viniRossi3, alt: "Vini Rossi - Amarone, Edizione Cinque Autoctoni, Valpolicella" },
  { src: images.viniRossi4, alt: "Vini Rossi - Negroamaro, Rossobastardo, Sherazade, Due Lune" },
];

const WineSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const { language } = useLanguage();

  const content = {
    it: { subtitle: "La Nostra Selezione", title: "Carta dei Vini" },
    de: { subtitle: "Unsere Auswahl", title: "Weinkarte" },
    en: { subtitle: "Our Selection", title: "Wine List" },
  };

  const t = content[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const openLightbox = (index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % wineImages.length);
  }, []);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + wineImages.length) % wineImages.length);
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, closeLightbox, goNext, goPrev]);

  return (
    <>
      <section
        id="vini"
        ref={sectionRef}
        data-testid="wine-section"
        className="py-20 bg-[#3d2617] relative overflow-hidden"
      >
        <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] bg-[#c9a227]/8 rounded-full blur-[100px]"></div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Header */}
          <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block text-[#c9a227] font-medium tracking-[0.2em] uppercase text-sm mb-4">{t.subtitle}</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">{t.title}</h2>
            <div className="w-24 h-0.5 bg-[#c9a227] mx-auto"></div>
          </div>

          {/* Wine Grid - 2x2 on desktop, 1 col on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {wineImages.map((wine, index) => (
              <div
                key={index}
                data-testid={`wine-card-${index}`}
                className={`cursor-pointer transition-all duration-700 delay-${index * 150} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                onClick={() => openLightbox(index)}
              >
                <div className="relative overflow-hidden rounded-lg border border-[#c9a227]/20 shadow-lg group">
                  <img
                    src={wine.src}
                    alt={wine.alt}
                    className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <span className="text-white text-sm font-medium bg-black/60 px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Ingrandisci
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          data-testid="wine-lightbox"
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            data-testid="lightbox-close"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-2"
          >
            <X size={32} />
          </button>

          {/* Prev button */}
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 text-white/80 hover:text-white z-10 p-2"
          >
            <ChevronLeft size={40} />
          </button>

          {/* Image */}
          <img
            src={wineImages[activeIndex].src}
            alt={wineImages[activeIndex].alt}
            data-testid="lightbox-image"
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 text-white/80 hover:text-white z-10 p-2"
          >
            <ChevronRight size={40} />
          </button>

          {/* Counter */}
          <div className="absolute bottom-4 text-white/60 text-sm">
            {activeIndex + 1} / {wineImages.length}
          </div>
        </div>
      )}
    </>
  );
};

export default WineSection;
